import { KikCustomerFlags, kikCustomerFlagsSchema } from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import {
  DataModelsSubscriptionStatus,
  SvcSubscribeUser,
  SvcUnsubscribeUser,
  UsersSubscriptionsSubscriptionDto,
} from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { DateTime } from "ts-luxon";

import { useAuthContext } from "~/contexts/auth";
import { useConfiguration } from "~/contexts/configuration";
import { useEvaService } from "~/hooks/use-eva-service";
import Logger from "~/services/logger/logger";
import loyaltyUtils from "~/utils/loyalty-utils";

interface KikCurrentTierRaw {
  AssignedAt: string;
  ExpiresAt: string;
  ID: string;
  Name: string;
}

interface KikCurrentTier extends Omit<KikCurrentTierRaw, "ExpiresAt"> {
  ExpiresAt: DateTime;
}

type KikLoyaltySubscription = UsersSubscriptionsSubscriptionDto & { CurrentTier: KikCurrentTierRaw };

const useSubscriptions = () => {
  const { isAuthenticated, user, getLoyaltyTiers, getUserAvailableSubscriptions, getUserSubscriptions } =
    useAuthContext();
  const { organizationUnitId: evaOrganizationUnitId } = useConfiguration();
  const router = useRouter();

  // Private

  const _isUserSubscriptionActive = (s?: { Status: number }) => s?.Status === 2;

  // Don't call the public MAPI endpoints on "default" locale pages (404 without locale prefix etc.):
  // x-locale would be "default" and MAPI rejects it
  const hasValidLocale = !!router.locale && router.locale !== "default";
  const canFetchPublicMapiData = !!evaOrganizationUnitId && hasValidLocale;

  // Public

  const evaServiceSubscribeUser = useEvaService(SvcSubscribeUser);
  const evaServiceUnsubscribeUser = useEvaService(SvcUnsubscribeUser);

  // Used to get the list of the loyalty tiers
  const { data: loyaltyTiers = [] } = useQuery({
    queryKey: ["mapiServiceGetLoyaltyTiers", evaOrganizationUnitId, router.locale],
    staleTime: 30 * 1000,
    cacheTime: 30 * 1000,
    refetchOnMount: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    queryFn: () => {
      return getLoyaltyTiers();
    },
    enabled: canFetchPublicMapiData,
  });

  // Used to get the list of all subscriptions
  const { data: { user: availableSubscriptions = [], loy: loyaltySubscriptions = [] } = {} } = useQuery({
    queryKey: ["mapiServiceGetAllSubscriptions", evaOrganizationUnitId, router.locale],
    staleTime: 30 * 1000,
    cacheTime: 30 * 1000,
    refetchOnMount: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    queryFn: () => {
      return getUserAvailableSubscriptions();
    },
    enabled: canFetchPublicMapiData,
  });

  // Used to get the list of all subscriptions
  const {
    data: userSubscriptions = { user: [], loy: [] },
    refetch: refetchUserSubscriptions,
    isLoading: isLoadingUserSubscriptions,
  } = useQuery({
    queryKey: ["evaServiceGetUserSubscriptions", user?.ID],
    staleTime: Infinity,
    queryFn: () => {
      return getUserSubscriptions();
    },
    enabled: isAuthenticated && !!user?.ID,
  });

  const getUserSubscriptionByBackendId = (BackendID: keyof KikCustomerFlags) => {
    return userSubscriptions.user.find((s) => s.Subscription?.BackendID === BackendID);
  };

  const getUserSubscriptionValueByBackendId = (BackendID: keyof KikCustomerFlags): boolean | void => {
    return _isUserSubscriptionActive(getUserSubscriptionByBackendId(BackendID));
  };

  const pushSubscriptionsByFlags = async (flags: Partial<KikCustomerFlags>) => {
    const subs =
      availableSubscriptions?.filter((s) => {
        return !!flags[s.BackendID as keyof KikCustomerFlags];
      }) || [];
    Logger.instance.debug("pushSubscriptionsByFlags called", {
      flags,
      filteredSubs: subs,
    });

    return subs.length
      ? evaServiceSubscribeUser({
          UserID: user?.ID,
          Subscriptions: subs.map((s) => {
            return {
              SubscriptionID: s.ID,
            };
          }),
        }).finally(() => refetchUserSubscriptions())
      : [];
  };

  const removeSubscriptionsByFlags = async (flags: Partial<KikCustomerFlags>): Promise<KikCustomerFlags> => {
    const subs =
      userSubscriptions.user?.filter((s) => {
        return (
          _isUserSubscriptionActive(s) &&
          flags.hasOwnProperty(s.Subscription?.BackendID as keyof KikCustomerFlags) &&
          !flags[s.Subscription?.BackendID as keyof KikCustomerFlags]
        );
      }) || [];
    Logger.instance.debug("removeSubscriptionsByFlags called", {
      flags,
      filteredSubs: subs,
    });
    const calls = subs.map((s) => {
      return evaServiceUnsubscribeUser({
        UserID: user?.ID,
        SubscriptionID: s.Subscription?.ID!,
      });
    });

    return Promise.all(calls)
      .finally(() => refetchUserSubscriptions())
      .then((results) => {
        return subs.reduce((acc, sub, index) => {
          acc[sub.Subscription?.BackendID as keyof KikCustomerFlags] =
            results[index]?.Status === DataModelsSubscriptionStatus.Unsubscribed;

          return acc;
        }, kikCustomerFlagsSchema.parse({}));
      });
  };

  const userSubscriptionsToFlags = (): Partial<Record<keyof KikCustomerFlags, boolean | void>> => {
    return (availableSubscriptions || []).reduce((acc, sub) => {
      const backendId = sub?.BackendID as keyof KikCustomerFlags;
      acc[backendId] = getUserSubscriptionValueByBackendId(backendId);

      return acc;
    }, {} as Partial<Record<keyof KikCustomerFlags, boolean | void>>);
  };

  const currentRawTier = (userSubscriptions.loy[0]?.Subscription as KikLoyaltySubscription)?.["CurrentTier"];
  const currentLoyaltyTier = useMemo(() => {
    return currentRawTier
      ? ({
          ...currentRawTier,
          ExpiresAt: DateTime.fromISO(currentRawTier.ExpiresAt),
        } satisfies KikCurrentTier)
      : void 0;
  }, [currentRawTier]);

  return {
    availableSubscriptions,
    currentLoyaltyTier,
    hasLoyaltyProgram: loyaltySubscriptions.length > 0,
    isLoadingUserSubscriptions,
    isUserSubscribedToLoyalty: loyaltyUtils.isSubscribedToLoyalty(userSubscriptions.loy),
    loyaltyIdentifier: userSubscriptions.loy[0]?.UserIdentifier,
    loyaltyPoints: userSubscriptions.loy[0]?.Subscription?.LoyaltyBalance?.Points,
    loyaltyTiers,
    loyaltyTierThresholds: loyaltyTiers.map((t) => t.threshold),
    userActiveSubscriptions: userSubscriptions.user?.filter(_isUserSubscriptionActive) || [],
    userSubscriptions,
    // Methods
    getUserSubscriptionByBackendId,
    getUserSubscriptionValueByBackendId,
    pushSubscriptionsByFlags,
    removeSubscriptionsByFlags,
    userSubscriptionsToFlags,
  };
};

export default useSubscriptions;
